import { useState } from 'react';
import type { DetailedHealthData } from '../types/health';

interface DetailedDataFormProps {
  onSave: (data: Partial<DetailedHealthData>) => void;
  onCancel: () => void;
  category: 'nutrition' | 'exercise' | 'sleep' | 'stress' | 'medicalHistory' | 'familyHistory' | 'wearableData' | 'labValues';
  existingData?: Partial<DetailedHealthData>;
}

export default function DetailedDataForm({ onSave, onCancel, category, existingData }: DetailedDataFormProps) {
  const [formData, setFormData] = useState<any>(existingData?.[category] || {});
  const [stressPractices, setStressPractices] = useState<string[]>(
    existingData?.stress?.managementPractices || []
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ [category]: formData });
  };

  const renderNutritionForm = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">Fruits & Vegetables per Day</label>
        <input
          type="number"
          min="0"
          max="20"
          value={formData.fruitsVeggiesDaily || ''}
          onChange={(e) => setFormData({ ...formData, fruitsVeggiesDaily: parseInt(e.target.value) })}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
          placeholder="5"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">Water Intake (glasses per day)</label>
        <input
          type="number"
          min="0"
          max="20"
          value={formData.waterIntake || ''}
          onChange={(e) => setFormData({ ...formData, waterIntake: parseInt(e.target.value) })}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
          placeholder="8"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">Processed Food Frequency</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['rarely', 'sometimes', 'often', 'daily'].map((freq) => (
            <button
              key={freq}
              type="button"
              onClick={() => setFormData({ ...formData, processedFoodFrequency: freq })}
              className={`px-4 py-3 rounded-lg border-2 transition-all capitalize whitespace-nowrap ${
                formData.processedFoodFrequency === freq
                  ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              {freq}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">Meal Regularity</label>
        <div className="grid grid-cols-3 gap-3">
          {['irregular', 'somewhat-regular', 'very-regular'].map((reg) => (
            <button
              key={reg}
              type="button"
              onClick={() => setFormData({ ...formData, mealRegularity: reg })}
              className={`px-4 py-3 rounded-lg border-2 transition-all whitespace-nowrap ${
                formData.mealRegularity === reg
                  ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              {reg.replace('-', ' ')}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderExerciseForm = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">Cardio Minutes per Week</label>
        <input
          type="number"
          min="0"
          max="1000"
          value={formData.cardioMinutesPerWeek || ''}
          onChange={(e) => setFormData({ ...formData, cardioMinutesPerWeek: parseInt(e.target.value) })}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
          placeholder="150"
        />
        <p className="text-sm text-gray-600 mt-2">Recommended: 150+ minutes per week</p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">Strength Training Days per Week</label>
        <input
          type="number"
          min="0"
          max="7"
          value={formData.strengthTrainingDays || ''}
          onChange={(e) => setFormData({ ...formData, strengthTrainingDays: parseInt(e.target.value) })}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
          placeholder="2"
        />
        <p className="text-sm text-gray-600 mt-2">Recommended: 2+ days per week</p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">Flexibility</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['poor', 'fair', 'good', 'excellent'].map((level) => (
            <button
              key={level}
              type="button"
              onClick={() => setFormData({ ...formData, flexibility: level })}
              className={`px-4 py-3 rounded-lg border-2 transition-all capitalize whitespace-nowrap ${
                formData.flexibility === level
                  ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              {level}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSleepForm = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">Average Hours per Night</label>
        <input
          type="number"
          min="0"
          max="24"
          step="0.5"
          value={formData.averageHoursPerNight || ''}
          onChange={(e) => setFormData({ ...formData, averageHoursPerNight: parseFloat(e.target.value) })}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
          placeholder="7.5"
        />
        <p className="text-sm text-gray-600 mt-2">Recommended: 7-9 hours</p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">Sleep Quality</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['poor', 'fair', 'good', 'excellent'].map((quality) => (
            <button
              key={quality}
              type="button"
              onClick={() => setFormData({ ...formData, quality })}
              className={`px-4 py-3 rounded-lg border-2 transition-all capitalize whitespace-nowrap ${
                formData.quality === quality
                  ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              {quality}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">Sleep Consistency</label>
        <div className="grid grid-cols-3 gap-3">
          {['irregular', 'somewhat-consistent', 'very-consistent'].map((consistency) => (
            <button
              key={consistency}
              type="button"
              onClick={() => setFormData({ ...formData, consistency })}
              className={`px-4 py-3 rounded-lg border-2 transition-all whitespace-nowrap ${
                formData.consistency === consistency
                  ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              {consistency.replace('-', ' ')}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStressForm = () => {
    const availablePractices = [
      'Meditation', 'Yoga', 'Deep Breathing', 'Exercise', 'Journaling',
      'Therapy/Counseling', 'Time in Nature', 'Hobbies', 'Social Connection'
    ];

    const togglePractice = (practice: string) => {
      const updated = stressPractices.includes(practice)
        ? stressPractices.filter(p => p !== practice)
        : [...stressPractices, practice];
      setStressPractices(updated);
      setFormData({ ...formData, managementPractices: updated });
    };

    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-3">Stress Level (1-10)</label>
          <input
            type="range"
            min="1"
            max="10"
            value={formData.level || 5}
            onChange={(e) => setFormData({ ...formData, level: parseInt(e.target.value) })}
            className="w-full"
          />
          <div className="flex justify-between text-sm text-gray-600 mt-2">
            <span>Low (1)</span>
            <span className="font-semibold text-gray-900">{formData.level || 5}</span>
            <span>High (10)</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-3">Stress Management Practices</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {availablePractices.map((practice) => (
              <button
                key={practice}
                type="button"
                onClick={() => togglePractice(practice)}
                className={`px-4 py-3 rounded-lg border-2 transition-all whitespace-nowrap ${
                  stressPractices.includes(practice)
                    ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                {practice}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-3">Work-Life Balance</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {['poor', 'fair', 'good', 'excellent'].map((balance) => (
              <button
                key={balance}
                type="button"
                onClick={() => setFormData({ ...formData, workLifeBalance: balance })}
                className={`px-4 py-3 rounded-lg border-2 transition-all capitalize whitespace-nowrap ${
                  formData.workLifeBalance === balance
                    ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}
              >
                {balance}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderLabValuesForm = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <i className="ri-information-line text-xl text-blue-600 mt-0.5"></i>
          <p className="text-sm text-gray-700">
            Enter your most recent lab values. These should come from medical tests performed by healthcare professionals.
          </p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">Blood Pressure</label>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <input
              type="number"
              placeholder="Systolic (120)"
              value={formData.bloodPressure?.systolic || ''}
              onChange={(e) => setFormData({
                ...formData,
                bloodPressure: { ...formData.bloodPressure, systolic: parseInt(e.target.value) }
              })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
            />
            <p className="text-xs text-gray-600 mt-1">Systolic</p>
          </div>
          <div>
            <input
              type="number"
              placeholder="Diastolic (80)"
              value={formData.bloodPressure?.diastolic || ''}
              onChange={(e) => setFormData({
                ...formData,
                bloodPressure: { ...formData.bloodPressure, diastolic: parseInt(e.target.value) }
              })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
            />
            <p className="text-xs text-gray-600 mt-1">Diastolic</p>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">Cholesterol (mg/dL)</label>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <input
              type="number"
              placeholder="Total"
              value={formData.cholesterol?.total || ''}
              onChange={(e) => setFormData({
                ...formData,
                cholesterol: { ...formData.cholesterol, total: parseInt(e.target.value) }
              })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
            />
            <p className="text-xs text-gray-600 mt-1">Total</p>
          </div>
          <div>
            <input
              type="number"
              placeholder="HDL"
              value={formData.cholesterol?.hdl || ''}
              onChange={(e) => setFormData({
                ...formData,
                cholesterol: { ...formData.cholesterol, hdl: parseInt(e.target.value) }
              })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
            />
            <p className="text-xs text-gray-600 mt-1">HDL</p>
          </div>
          <div>
            <input
              type="number"
              placeholder="LDL"
              value={formData.cholesterol?.ldl || ''}
              onChange={(e) => setFormData({
                ...formData,
                cholesterol: { ...formData.cholesterol, ldl: parseInt(e.target.value) }
              })}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
            />
            <p className="text-xs text-gray-600 mt-1">LDL</p>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-900 mb-3">Fasting Blood Sugar (mg/dL)</label>
        <input
          type="number"
          placeholder="90"
          value={formData.bloodSugar || ''}
          onChange={(e) => setFormData({ ...formData, bloodSugar: parseInt(e.target.value) })}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
        />
      </div>
    </div>
  );

  const categoryTitles = {
    nutrition: 'Nutrition Habits',
    exercise: 'Exercise Routine',
    sleep: 'Sleep Patterns',
    stress: 'Stress & Mental Wellbeing',
    medicalHistory: 'Medical History',
    familyHistory: 'Family History',
    wearableData: 'Wearable Data',
    labValues: 'Lab Values'
  };

  const categoryIcons = {
    nutrition: 'ri-restaurant-line',
    exercise: 'ri-run-line',
    sleep: 'ri-moon-line',
    stress: 'ri-mental-health-line',
    medicalHistory: 'ri-medicine-bottle-line',
    familyHistory: 'ri-parent-line',
    wearableData: 'ri-watch-line',
    labValues: 'ri-test-tube-line'
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
          <i className={`${categoryIcons[category]} text-3xl text-teal-600`}></i>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{categoryTitles[category]}</h2>
        <p className="text-gray-600">Add detailed information to improve your score accuracy</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border-2 border-gray-200 p-8">
        {category === 'nutrition' && renderNutritionForm()}
        {category === 'exercise' && renderExerciseForm()}
        {category === 'sleep' && renderSleepForm()}
        {category === 'stress' && renderStressForm()}
        {category === 'labValues' && renderLabValuesForm()}

        <div className="flex justify-between pt-8 mt-8 border-t-2 border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-gray-400 transition-colors whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-8 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap"
          >
            Save Data
          </button>
        </div>
      </form>
    </div>
  );
}
